import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useClient } from "#/hook/useClient";
import type { PredictionBoard, ShipmentCard } from "#/types/prediction";
import { getClientBrandColor, getContrastingTextColor } from "#/utils/ColorUtils";
import { daysOfWeek, formatWeekday, isSameLocalDay, localDayKey } from "#/utils/dateTimeUtils";
import { anchorOf } from "../predictionUtils";
import ShipmentBlock from "./ShipmentBlock";

interface Props {
  board: Pick<PredictionBoard, "categories">;
  cards: ShipmentCard[];
  /** Monday 00:00 local of the week on display. */
  weekStart: Date;
  now: Date;
  onOpen: (shipmentId: number) => void;
}

/**
 * The week, Monday to Sunday. Each day is a raised panel on a darker page
 * background (rounded, bordered, shadowed) with a header band showing the
 * weekday and the date, so the days read as seven surfaces rather than a flat
 * grid of lines. Today is marked the way calendar apps do it: the date in a
 * filled brand-colour circle, the header band tinted in the brand colour and
 * the panel lifted with a brand ring. No day is greyed out. Each shipment is
 * one card on its day; several on one day stack. Empty days stay empty.
 */
const PredictionWeekCalendar = ({ cards, board, weekStart, now, onOpen }: Props) => {
  const { i18n } = useTranslation();
  const { client } = useClient();
  // Brand colours range from near-black to pale yellow: pick the day number's
  // text colour from the real background instead of assuming white.
  const brand = client?.color ?? getClientBrandColor();
  const todayText = getContrastingTextColor(brand);

  const days = useMemo(() => daysOfWeek(weekStart), [weekStart]);

  const byDay = useMemo(() => {
    const grouped = new Map<string, ShipmentCard[]>();
    for (const card of cards) {
      const key = localDayKey(card.shipment.shipmentDate);
      if (!key) continue;
      const list = grouped.get(key) ?? [];
      list.push(card);
      grouped.set(key, list);
    }
    // One day, one order: by the anchor sub-client's name, so re-renders never shuffle cards.
    for (const list of grouped.values()) {
      list.sort((a, b) =>
        (anchorOf(a)?.subClient.name ?? "").localeCompare(anchorOf(b)?.subClient.name ?? "")
      );
    }
    return grouped;
  }, [cards]);

  return (
    <div className="min-h-0 flex-1 overflow-auto rounded-xl bg-slate-200/70 p-2">
      <div className="grid min-h-full min-w-[56rem] grid-cols-7 gap-2">
        {days.map((day) => {
          const isToday = isSameLocalDay(day, now);
          const key = localDayKey(day);
          const list = key ? (byDay.get(key) ?? []) : [];
          return (
            <section
              key={day.toISOString()}
              aria-current={isToday ? "date" : undefined}
              className={`flex min-w-0 flex-col overflow-hidden rounded-lg border bg-white ${
                isToday
                  ? "border-[var(--client-color,#3f81ef)] shadow-md ring-2 ring-[var(--client-color,#3f81ef)]/30"
                  : "border-gray-200 shadow-sm"
              }`}
            >
              <header
                className={`sticky top-0 z-10 flex flex-col items-center gap-1 border-b px-2 pt-3 pb-2 ${
                  isToday
                    ? "border-[color-mix(in_srgb,var(--client-color,#3f81ef)_35%,white)] bg-[color-mix(in_srgb,var(--client-color,#3f81ef)_10%,white)]"
                    : "border-gray-200 bg-gray-50"
                }`}
              >
                <span
                  className={`text-[11px] font-semibold tracking-wide uppercase ${
                    isToday ? "text-[var(--client-color,#3f81ef)]" : "text-gray-500"
                  }`}
                >
                  {formatWeekday(day, i18n.language, "short")}
                </span>
                <span
                  className={`flex h-9 w-9 items-center justify-center rounded-full text-xl tabular-nums ${
                    isToday
                      ? "bg-[var(--client-color,#3f81ef)] font-semibold shadow"
                      : "font-medium text-gray-800"
                  }`}
                  style={isToday ? { color: todayText } : undefined}
                >
                  {day.getDate()}
                </span>
              </header>
              <div className="flex flex-1 flex-col gap-2 p-2">
                {list.map((card) => (
                  <ShipmentBlock
                    key={card.shipment.id}
                    card={card}
                    board={board}
                    now={now}
                    onOpen={onOpen}
                  />
                ))}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
};

export default PredictionWeekCalendar;
